const GLYPH = '█';
const SRC = 'qlk-src';      // hidden wrapper around Angular's own text node
const OUT = 'qlk-out';      // our visible, colorized mirror
const SWATCH = 'qlk-swatch';

// Qlik variables that drive the legend. Both re-evaluate on selection changes
// (they fold in the effect of v_ind_RR_dd), so we subscribe rather than read once.
const VAR_COUNT = 'v_sq_num';    // number of glyphs expected in the string
const VAR_COLORS = 'v_sq_clrs';  // ';'-separated hex per glyph, e.g. 'ffd800;d3e9ff'

// Current snapshot, updated together from a single session object.
let currentColors = [];          // ['#ffd800', '#d3e9ff', ...] (may contain null = skip)
let currentNum = 0;
let currentSig = '';             // signature of the snapshot, for staleness checks

const HEX_RE = /^#?[0-9a-fA-F]{3,8}$/;

function parseColors(raw) {
  // Keep empty entries as null (a glyph with no assigned colour) so indices stay
  // aligned even with strings like 'ffd800;;d3e9ff'. Do NOT filter them out.
  return String(raw == null ? '' : raw)
    .split(';')
    .map((c) => {
      const t = c.trim();
      return HEX_RE.test(t) ? (t[0] === '#' ? t : '#' + t) : null;
    });
}

function parseNum(raw) {
  const n = Number.parseInt(String(raw == null ? '' : raw).replace(',', '.'), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function colourFor(index) {
  // Assigned colour if present; otherwise fall back to the last defined colour so
  // an extra glyph never flashes black. Null (explicitly empty) means leave default.
  if (index < currentColors.length) return currentColors[index];
  for (let j = currentColors.length - 1; j >= 0; j -= 1) {
    if (currentColors[j]) return currentColors[j];
  }
  return null;
}

function colorize(out, text) {
  out.textContent = '';
  let i = 0;
  text.split(/(█)/).forEach((part) => {
    if (!part) return;
    if (part === GLYPH) {
      const span = document.createElement('span');
      span.className = SWATCH;
      span.textContent = GLYPH.repeat(2);      // doubled width, from the glyph itself
      const colour = colourFor(i);
      if (colour) span.style.color = colour;   // inline: beats the h2's inherited black
      out.appendChild(span);
      i += 1;
    } else {
      out.appendChild(document.createTextNode(part));
    }
  });
  out.dataset.src = text;
  out.dataset.sig = currentSig;                // repaint when the snapshot changes
}

function processTitle(titleEl) {
  // 1. Re-sync mirrors whose source text OR colour snapshot has changed.
  titleEl.querySelectorAll('.' + OUT).forEach((out) => {
    const src = out.previousElementSibling;
    if (!src || !src.classList.contains(SRC)) {
      out.remove();                            // Angular rebuilt the subtree; start over
      return;
    }
    const text = src.textContent;
    if (text !== out.dataset.src || currentSig !== out.dataset.sig) {
      if (text.indexOf(GLYPH) === -1) { src.classList.remove(SRC); out.remove(); }
      else colorize(out, text);
    }
  });

  // 2. Adopt any text node carrying the glyph that isn't wrapped yet.
  const walker = document.createTreeWalker(titleEl, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const p = n.parentElement;
      if (!p || n.nodeValue.indexOf(GLYPH) === -1) return NodeFilter.FILTER_REJECT;
      if (p.classList.contains(SRC) || p.closest('.' + OUT)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const fresh = [];
  while (walker.nextNode()) fresh.push(walker.currentNode);

  fresh.forEach((node) => {
    const src = document.createElement('span');
    node.parentNode.insertBefore(src, node);
    src.appendChild(node);                     // move, don't replace — keeps Angular's ref live
    const out = document.createElement('span');
    out.className = OUT;
    src.parentNode.insertBefore(out, src.nextSibling);
    colorize(out, node.nodeValue);
    src.className = SRC;                       // hide only once the mirror is in place
  });
}

/* ---------- Qlik variable subscription (both vars in one snapshot) ---------- */

function watchLegendVars(app, onSnapshot) {
  if (!app) return () => {};

  const def = {
    qInfo: { qType: 'legend-swatch-probe' },
    num:   { qStringExpression: { qExpr: '=' + VAR_COUNT } },
    clrs:  { qStringExpression: { qExpr: '=' + VAR_COLORS } },
  };

  const enigmaApp =
    (app.model && app.model.enigmaModel) ||
    app.enigmaModel ||
    (typeof app.createSessionObject === 'function' ? app : null);

  if (enigmaApp) {
    let obj = null;
    let disposed = false;
    const read = () =>
      obj && obj.getLayout().then((l) => onSnapshot(l.num, l.clrs)).catch(() => {});
    enigmaApp.createSessionObject(def).then((o) => {
      if (disposed) { enigmaApp.destroySessionObject(o.id).catch(() => {}); return; }
      obj = o;
      o.on('changed', read);
      read();
    }).catch(() => {});
    return () => {
      disposed = true;
      if (!obj) return;
      if (obj.removeAllListeners) obj.removeAllListeners('changed');
      enigmaApp.destroySessionObject(obj.id).catch(() => {});
      obj = null;
    };
  }

  // Capability API fallback — the callback re-fires on every validation.
  if (typeof app.createGenericObject === 'function') {
    let model = null;
    let disposed = false;
    Promise.resolve(app.createGenericObject(def, (reply) => onSnapshot(reply.num, reply.clrs)))
      .then((m) => {
        if (disposed && m && app.destroySessionObject) app.destroySessionObject(m.id);
        else model = m;
      })
      .catch(() => {});
    return () => {
      disposed = true;
      if (model && app.destroySessionObject) app.destroySessionObject(model.id);
    };
  }

  return () => {};
}

/* ---------- lifecycle ---------- */

let observer = null;
let frame = null;
let watched = [];
let observedRoot = null;

const OBS_OPTS = {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['title'],
};

function paintAll() {
  watched.forEach((id) => {
    document.querySelectorAll(`[id="${id}_title"]`).forEach((header) => {
      header.querySelectorAll('.qv-object-title').forEach(processTitle);
    });
  });
}

export function refreshLegendSwatches() {
  if (!observer) { paintAll(); return; }
  observer.disconnect();
  paintAll();
  observer.observe(observedRoot || document.body, OBS_OPTS);
}

export function initLegendSwatches(objectIds, options = {}) {
  const { app = null, root = document.body } = options;
  watched = Array.from(new Set(objectIds));
  observedRoot = root;

  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      observer.disconnect();                   // don't observe our own writes
      paintAll();
      observer.observe(root, OBS_OPTS);
    });
  };

  observer = new MutationObserver(schedule);
  paintAll();
  observer.observe(root, OBS_OPTS);

  const unwatch = watchLegendVars(app, (rawNum, rawClrs) => {
    const nextColors = parseColors(rawClrs);
    const nextNum = parseNum(rawNum);
    const nextSig = nextNum + '|' + nextColors.map((c) => c || '').join(';');
    if (nextSig === currentSig) return;        // nothing actually changed
    currentColors = nextColors;
    currentNum = nextNum;
    currentSig = nextSig;
    refreshLegendSwatches();
  });

  return () => {
    unwatch();
    if (frame) cancelAnimationFrame(frame);
    observer.disconnect();
    observer = null;
    frame = null;
    watched = [];
    observedRoot = null;
    currentColors = [];
    currentNum = 0;
    currentSig = '';
  };
}